import React from "react";
import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import { buildCanonicalUrl } from "lib/seo";

import {
  fetchNavbarAndFooter,
  fetchPaywallByIdServer,
} from "lib/fetchers/common";
import { fetchRankingPageData } from "lib/fetchers/rankings";
import type { RankingData } from "lib/fetchers/rankings";

import RankingView from "../../content-ui/rankings/rankingView";
import PreviewModeBanner from "components/previewModeBanner/previewModeBanner";
import ErrorBoundary from "components/errorBoundary/errorBoundary";
import Navigation from "components/navigation/navigation";
import Footer from "components/footer/footer";
import SponsoredBanner from "components/sponsoredBanner/sponsoredBanner";
import { cleanSlugsFromDecodedQueryParameters } from "lib/utils";
import { makeDecisionServer } from "lib/zephr/public-api";
import { DecisionAction } from "lib/zephr/types";
import { PaywallWindow } from "lib/types/paywalls";
import { paywallRanking } from "content-ui/rankings/paywallRanking";
import { ClientOnlyPaywall } from "components/paywallModal/clientOnlyPaywall";

export async function getServerSideProps({
  res,
  req,
  params,
  preview = false,
  query,
  resolvedUrl,
}) {
  const blaizeSession = req.cookies["blaize_session"] || "";

  const cleanSlug = cleanSlugsFromDecodedQueryParameters(params.slug)[0];
  const rankingData = await fetchRankingPageData({
    slug: cleanSlug,
    preview,
    extraLogs: {},
  });

  const decision = await makeDecisionServer(
    blaizeSession,
    "rankings-sdk",
    {
      is_pro: rankingData?.isPro ?? true,
      slug: cleanSlug,
    },
    req
  );

  const downloadDecision = await makeDecisionServer(
    blaizeSession,
    "rankings-data-download-sdk",
    {
      content_type: "ranking-download",
    },
    req
  );

  const [menuItems, paywall, downloadPaywall] = await Promise.all([
    fetchNavbarAndFooter(),
    decision !== undefined &&
    decision?.action === DecisionAction.DISPLAY_PAYWALL
      ? fetchPaywallByIdServer(decision?.payload?.paywallId)
      : null,
    downloadDecision !== undefined &&
    downloadDecision?.action === DecisionAction.DOWNLOAD_PAYWALL
      ? fetchPaywallByIdServer(downloadDecision?.payload?.paywallId)
      : null,
  ]);

  res.setHeader(
    "Cache-Control",
    "public, s-maxage=10, stale-while-revalidate=59"
  );

  if (!rankingData) return { notFound: true };

  return {
    props: {
      rankingData:
        decision?.action === DecisionAction.NONE
          ? rankingData
          : paywallRanking(rankingData),
      hideContent: decision?.action !== DecisionAction.NONE,
      paywall,
      downloadPaywall,
      slug: cleanSlug,
      canonical: buildCanonicalUrl(`/rankings/${cleanSlug}`),
      previewMode: preview,
      __initialState: {
        sitewideOption: { ...menuItems },
      },
    },
  };
}

type PageProps = {
  paywall: PaywallWindow | null;
  downloadPaywall: PaywallWindow | null;
  hideContent: boolean;
  rankingData: RankingData;
  previewMode: boolean;
  slug: string;
  canonical: string;
};

const RankingPage = (props: PageProps) => {
  const { title, description, ...dato } = props?.rankingData;
  const seoImage = props?.rankingData?.rankingImage?.url;

  return (
    <>
      <Head>
        {generateNextSeo({
          title: title,
          description: description,
          canonical: props.canonical,
          openGraph: {
            title: title,
            description: description,
            images: [{ url: seoImage }],
          },
          twitter: {
            handle: "@siftedeu",
            site: "@siftedeu",
            cardType: "summary_large_image",
          },
        })}
        <link rel="icon" href="/favicon.ico" />
      </Head>

      <Navigation
        stickyContent={
          <>
            {dato.hasStickyDescriptionBar && dato.isSponsored && (
              <SponsoredBanner
                sponsorshipType={dato.sponsorshipType}
                data={{
                  withProgressBar: false,
                  name: dato.sponsorName,
                  description: dato.sponsorDescription,
                  url: dato.sponsorUrl,
                  logo: dato.sponsorImage?.url,
                }}
              />
            )}
          </>
        }
      />

      {props.previewMode ? (
        <PreviewModeBanner exitSlug={`/rankings/${props.slug}`} />
      ) : null}

      <ErrorBoundary>
        <>
          {props.paywall !== null && (
            <ClientOnlyPaywall paywall={props.paywall!} />
          )}
          <RankingView
            ranking={props.rankingData}
            previewMode={props.previewMode}
            blur={props.hideContent}
            downloadPaywall={props.downloadPaywall}
          />
        </>
      </ErrorBoundary>

      <Footer />
    </>
  );
};

export default RankingPage;
